import { ChangeDetectionStrategy, Component, inject, input, output } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Button2Component } from '@frontendlib/components/components/button/button2.component';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { DateFormatPipe } from '@modules/common/pipes/date-format.pipe';
import { DragNDropModule } from '@modules/drag-n-drop/drag-n-drop.module';
import { type DragData } from '@modules/drag-n-drop/types/drag-data';
import { StitchBottomBarComponent } from '@modules/elements/components/stitch-bottom-bar/stitch-bottom-bar.component';
import { BoardsFolderContextMenuDirective } from '../../menus/boards-folder-context-menu.directive';
import { BoardActionsService } from '../../../services/board-actions.service';
import { BoardsStore } from '../../../services/boards.store';
import { boardsFromDragData } from '../../shared/board-drag-data.util';
import { type BoardFolderRow } from '../../shared/board-folder-rows.util';

/** One folder in a folder list — the Boards page's "Folders" view and the dynamic panel's — as a list card. */
@Component({
  selector: 'app-boards-folder-card',
  templateUrl: './boards-folder-card.component.html',
  styleUrls: ['../../../../common/components/base-stitch/base-stitch.component.less'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
  imports: [Button2Component, IconComponent, MatTooltipModule, DateFormatPipe, DragNDropModule, StitchBottomBarComponent, BoardsFolderContextMenuDirective],
})
export class BoardsFolderCardComponent {
  public readonly folder = input.required<BoardFolderRow>();
  /** Open the folder; `true` from the context menu's "Select all", which opens it with its boards selected. */
  public readonly open = output<boolean>();
  /** The context menu's "Duplicate" made this folder. */
  public readonly duplicated = output<string>();
  /** The context menu's "New board" made this board in the folder. */
  public readonly boardCreated = output<string>();

  protected readonly store = inject(BoardsStore);
  protected readonly folderActions = inject(BoardActionsService);

  /** A board can be dropped onto a folder card to move it there — the same operation the Boards sidebar and
   * Mail offer (SV1-481 review). Moving also restores the board out of Archive/Trash (see devdoc/specs/boards.md). */
  protected readonly dropPredicate = (dragData: DragData): boolean =>
    boardsFromDragData(dragData).some(board => this.store.boards().some(local => local.id === board.id));

  protected drop(dragData: DragData): void {
    const folder = this.store.folders().find(item => item.id === this.folder().id);
    if (!folder || folder.archived || folder.deleted) return;
    const boardIds = boardsFromDragData(dragData).filter(board => this.store.boards().some(local => local.id === board.id)).map(board => board.id);
    if (boardIds.length) this.store.moveBoardsToFolder(boardIds, folder.id);
  }
}
